import { Heading, Text, VStack } from '@/components/Atoms'
import { FuzeLogo } from '@/components/Logo'
import { Spinner } from '@/components/Spinner'
import config from '@/config'
import { useAuthConfig } from '@/hooks/queries/useAuthConfig'
import { setStoredRedirect } from '@/stores/redirect'
import { getSessionToken } from '@/stores/session'
import { Button, Separator } from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { UserIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirect: z.string().optional(),
    error: z.enum(['signin', 'notinvited']).optional(),
  }),
  beforeLoad: ({ location, search }) => {
    const sessionToken = getSessionToken()
    const parsed = z.object({ redirect: z.string().optional() }).parse(search)

    if (sessionToken) {
      throw redirect({
        to: parsed.redirect || '/welcome',
        search: {
          redirect: location.href,
        },
      })
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation('login')
  const { data, isSuccess, isLoading, error } = useAuthConfig()
  const { redirect, error: loginError } = Route.useSearch()

  if (redirect) {
    setStoredRedirect({
      path: redirect,
      search: {},
    })
  }

  if (error) return <div>{t('We have some trouble right now. Please come back later.')}</div>

  const generateHref = () => {
    if (data) {
      const { SSO_AUTHORIZATION_URL, SSO_CLIENT_ID, scope } = data
      const encodedScope = encodeURIComponent(scope.join(' '))
      const callbackURL = `${config.backendURL}/auth/callback`

      // Force the account picker after a failed sign-in so users stuck on a
      // disabled account (silently re-authenticated via the IdP SSO cookie)
      // can switch to a different account.
      const promptParam = loginError ? '&prompt=select_account' : ''

      return `${SSO_AUTHORIZATION_URL}?response_type=code&client_id=${SSO_CLIENT_ID}&redirect_uri=${encodeURI(callbackURL)}&scope=${encodedScope}${promptParam}`
    }
  }

  const href = isSuccess ? generateHref() : '#'

  return (
    <div className="grid min-h-svh grid-cols-2">
      <div className="flex flex-col gap-4 p-10">
        <div className="flex h-6 justify-start gap-2">
          <a href="#" className="flex items-center gap-2 font-medium">
            <FuzeLogo type="full" className="h-6 w-auto" />
          </a>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <VStack gap="2xl" align="center">
              <VStack gap="md" align="center">
                <Heading level={1}>{t('Welcome to Fuze')}</Heading>
                <Text tone="muted" size="md" align="center" leading="tight" className="text-pretty">
                  {t("You'll need to sign in using your BAT account to continue.")}
                </Text>
              </VStack>

              {loginError && (
                <div className="border-destructive/30 bg-destructive/10 w-full rounded-md border p-3">
                  <Text
                    size="xs"
                    align="center"
                    leading="tight"
                    className="text-destructive text-pretty"
                  >
                    {loginError === 'notinvited'
                      ? t('account.notinvited.error')
                      : t('account.signin.error')}
                  </Text>
                </div>
              )}

              <VStack gap="md" align="center" className="w-full">
                <a
                  href={href}
                  className={cn(
                    'bg-primary hover:bg-primary/90 my-3 flex w-full select-none items-center justify-center gap-2 rounded-md px-3 py-2.5 text-center font-medium text-white transition-all',
                    isLoading && 'pointer-events-none opacity-50',
                  )}
                >
                  {isLoading ? (
                    <>
                      <Spinner color="inverted" size="sm" />
                      <span>{t('Fetching data...')}</span>
                    </>
                  ) : (
                    <>
                      <UserIcon className="size-4" />
                      <span>{t('Sign in')}</span>
                    </>
                  )}
                </a>
                <Separator />
                <Text tone="muted" align="center" leading="tight" className="text-pretty">
                  {t('Need help signing in?')}{' '}
                  <Button
                    variant="link"
                    className="text-muted-foreground hover:text-foreground px-1"
                  >
                    {t('Contact support')}
                  </Button>
                </Text>
              </VStack>
            </VStack>
          </div>
        </div>

        <div className="flex h-6 items-end justify-start gap-2">
          <Text
            tone="muted"
            align="center"
            leading="tight"
            className="flex items-center gap-1 text-pretty"
          >
            {t('A British American Tobacco Application')}
          </Text>
        </div>
      </div>

      <div className="relative block py-2 pr-2">
        <div className="from-foreground to-foreground/80 relative h-full w-full overflow-clip rounded-xl bg-gradient-to-t shadow-xl">
          <motion.div
            className="top-7/8 absolute left-1/4 size-[150%]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
          >
            <FuzeLogo type="icon" className="h-full w-full text-zinc-800" />
          </motion.div>
        </div>
      </div>
    </div>
  )
}
